import React, { useMemo, useState } from 'react';
import { IonSelect, IonSelectOption, IonSpinner } from '@ionic/react';
import { parse, format } from 'date-fns';
import { useAdditionalContent } from '@/hooks/useAdditionalContent';
import type { MarketPulseData, MarketPulseState } from '@/types/marketPulse';
import { UsStates } from '@/types/marketPulse';

const MARKET_PULSE_ICON_URL = 'https://storage.googleapis.com/rev-public/icons/Market%20Pulse.svg';

interface MarketPulseProps {
  className?: string;
}

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatPercent(value: string | number): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function formatNumber(value: string | number): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString();
}

function formatDate(value: string): string {
  const trimmed = value.trim();
  
  try {
    // Handle "YYYY-MM-DD HH:mm:ss.SSSSSS" format (with microseconds)
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d+$/.test(trimmed)) {
      const parsed = parse(trimmed, 'yyyy-MM-dd HH:mm:ss.SSSSSS', new Date());
      return format(parsed, 'MMM d, yyyy');
    }
    
    // Handle "YYYY-MM-DD" format
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const parsed = parse(trimmed, 'yyyy-MM-dd', new Date());
      return format(parsed, 'MMM d, yyyy');
    }
    
    // Fallback: return original value
    return value;
  } catch {
    return value;
  }
}

export const MarketPulse: React.FC<MarketPulseProps> = ({ className }) => {
  const { content, loading } = useAdditionalContent({ page: 'home', slot: 'market_pulse' });
  const [selectedStateCode, setSelectedStateCode] = useState<string | undefined>(undefined);

  // Parse Market Pulse: accept "us"|"national"|"nationalAverage" and "states"|"stateAverages"
  const mp: MarketPulseData | null = useMemo(() => {
    if (!content || content.length === 0) return null;
    const item = content[0];
    if (!item?.json) return null;
    try {
      const raw = typeof item.json === 'string' ? JSON.parse(item.json) : item.json;
      if (!raw || typeof raw !== 'object') return null;
      const data = raw as Record<string, unknown>;
      const national =
        data.nationalAverage ?? data.national ?? data.us;
      const statesArray =
        data.stateAverages ?? data.states;
      const usObj =
        national && typeof national === 'object' && String((national as Record<string, unknown>).locale ?? '').toLowerCase() === 'us'
          ? (national as MarketPulseData['us'])
          : null;
      const states = Array.isArray(statesArray) ? statesArray as MarketPulseData['states'] : [];
      if (!usObj) return null;
      return { us: usObj, states };
    } catch {
      return null;
    }
  }, [content]);

  const selectedState: MarketPulseState | undefined = useMemo(() => {
    if (!selectedStateCode || !mp) return undefined;
    return mp.states.find(s => s.locale.toLowerCase() === selectedStateCode.toLowerCase());
  }, [selectedStateCode, mp]);

  const selectedStateName = useMemo(() => {
    if (!selectedStateCode) return undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (UsStates as any)[selectedStateCode] as string | undefined;
  }, [selectedStateCode]);

  // Show loading state
  if (loading) {
    return (
      <aside className={`justify-center items-stretch shadow-[0_14px_42px_0_rgba(8,15,52,0.06)] bg-white self-stretch flex min-w-60 w-full flex-col overflow-hidden flex-1 shrink basis-[0%] my-auto p-3 rounded-[10px] ${className || ''}`}>
        <div className="flex items-center justify-center h-64">
          <IonSpinner name="crescent" />
        </div>
      </aside>
    );
  }

  // Show empty state if no data
  if (!mp) {
    return null;
  }

  return (
    <aside className={`justify-center items-stretch shadow-[0_14px_42px_0_rgba(8,15,52,0.06)] bg-white self-stretch flex min-w-60 w-full flex-col overflow-hidden flex-1 shrink basis-[0%] my-auto p-3 rounded-[10px] ${className || ''}`}>
      <img
        src={MARKET_PULSE_ICON_URL}
        alt="Market pulse icon"
        className="aspect-[1] object-contain w-10"
      />
      <div className="w-full mt-2.5 flex-1 flex flex-col">
        <header className="w-full font-medium">
          <h2 className="text-[#2D2D2D] text-xl leading-none tracking-[-0.4px]">
            Market Pulse Overview
          </h2>
          <p className="text-[#928881] text-sm">
            Updated: {formatDate(mp.us.date)}
          </p>
        </header>
        <div className="flex w-full items-center gap-[18px] justify-between mt-[10px]">
          <div className="self-stretch flex flex-col items-stretch justify-center my-auto">
            <div className="text-[#D44D5C] text-base font-semibold leading-none tracking-[-0.32px]">
              ${formatCurrency(mp.us.averageRent)}
            </div>
            <div className="text-[#2D2D2D] text-xs font-normal text-center mt-1">
              Avg Lease Value
            </div>
          </div>
          <div className="self-stretch flex flex-col items-stretch justify-center my-auto">
            <div className="text-[#D44D5C] text-base font-semibold leading-none tracking-[-0.32px]">
              {formatPercent(mp.us.occupancyRate)}%
            </div>
            <div className="text-[#2D2D2D] text-xs font-normal text-center mt-1">
              Occupancy Rate
            </div>
          </div>
          <div className="self-stretch flex flex-col items-stretch justify-center my-auto">
            <div className="text-[#D44D5C] text-base font-semibold leading-none tracking-[-0.32px]">
              {formatNumber(mp.us.activeListings)}
            </div>
            <div className="text-[#2D2D2D] text-xs font-normal text-center mt-1">
              Active Listings
            </div>
          </div>
        </div>
        <div className="bg-[#D44D5C]/10 flex w-full flex-col shrink-0 text-sm font-medium justify-start mt-[12px] px-3 pb-3 rounded-[10px] grow-0" style={{ height: 112 }}>
          <div>
            <IonSelect
              className="market-pulse-select"
              placeholder="Select your state"
              value={selectedStateCode}
              onIonChange={(e) => setSelectedStateCode(e.detail.value)}
              interface="popover"
            >
              {Object.entries(UsStates).map(([code, name]) => (
                <IonSelectOption key={code} value={code}>
                  {name as string}
                </IonSelectOption>
              ))}
            </IonSelect>
          </div>
          {selectedState && selectedStateName && (
            <div className="text-[#2D2D2D]">
              As of {formatDate(selectedState.date)}, the average lease value is <span className="font-semibold color-primary">${formatCurrency(selectedState.averageRent)}</span> with a <span className="font-semibold color-primary">{formatPercent(selectedState.occupancyRate)}%</span> occupancy rate and <span className="font-semibold color-primary">{formatNumber(selectedState.activeListings)}</span> active listings.
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};
